// The operator's identity, read at boot from /brand.json.
//
// THE SAME DOCUMENT THE SERVER READS. `services.meetnix.branding` writes
// brand.json into the static directory; the Go side loads it in internal/brand
// and this loads it here. One file, so the guest join page and the app cannot end
// up calling the instance two different things — which is exactly what happens
// when branding is a set of flags, because the browser cannot see flags.
//
// Colours are NOT here. They arrive as /brand.css, which the page loads after
// theme.css so the operator's palette overrides the defaults through the cascade
// — CSS is better at that than we would be, and it means a colour change never
// re-renders anything.

export interface Brand {
  name: string;
  shortName: string;
  tagline: string;
  /** Whose instance this is, or null when the operator named none. */
  organization: string | null;
  signInLabel: string;
  /** Who the written record is open to, in prose. Shown before anyone commits. */
  audience: string;
  /** URL of the mark, or null — in which case the name is set as text. */
  logo: string | null;
  favicon: string | null;
  supportURL: string | null;
  /**
   * The AGPL §13 offer: where this build's source can be had, and which
   * revision it actually is. Independent of each other — an instance with no
   * URL still publishes its revision, which is a smaller offer but a true one.
   */
  sourceURL: string | null;
  sourceRev: string | null;
  /** The sentence naming where recordings are kept, or null if unstated. */
  recordingNotice: string | null;
}

/** The unbranded instance: complete and coherent, not a stub. */
const DEFAULTS: Brand = {
  name: 'Meetnix',
  shortName: 'Meetnix',
  tagline: 'Every voice on its own track, on your own iron.',
  organization: null,
  signInLabel: 'Sign in',
  audience: 'everyone who can sign in',
  logo: null,
  favicon: null,
  supportURL: null,
  sourceURL: null,
  sourceRev: null,
  recordingNotice: null,
};

let current: Brand = DEFAULTS;

/**
 * The active brand. Safe to call during render: loadBrand() runs to completion
 * before the first render, and nothing mutates this afterwards — a brand change
 * is a new store path and a service restart, so there is no live update to
 * subscribe to and no re-render to trigger.
 */
export function brand(): Brand {
  return current;
}

/**
 * Fetch /brand.json and merge it over the defaults.
 *
 * Never rejects. A missing or malformed file leaves the defaults in place,
 * because the name on the top bar is not worth a blank page — this runs before
 * the first render, so a throw here would take the whole app with it.
 */
export async function loadBrand(): Promise<void> {
  try {
    // no-store rather than no-cache: this is one small file fetched once per
    // page load, and a stale copy would show the previous brand after a deploy.
    const res = await fetch('/brand.json', { cache: 'no-store' });
    if (!res.ok) return;
    const got: unknown = await res.json();
    if (!got || typeof got !== 'object') return;

    const merged = { ...DEFAULTS };
    for (const [key, value] of Object.entries(got as Record<string, unknown>)) {
      if (!(key in merged)) continue; // ignore fields this app does not use (e.g. palette)
      // An absent or blank string keeps the default; `tagline` is the one field
      // where "" is a deliberate choice ("omit it"), so it is taken as given.
      if (typeof value === 'string') {
        if (value !== '' || key === 'tagline') {
          (merged as Record<string, unknown>)[key] = value;
        }
      } else if (value === null) {
        (merged as Record<string, unknown>)[key] = null;
      }
    }
    current = merged as Brand;
  } catch {
    // Keep the defaults.
  }
}
